import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AiOutlineStar, AiFillStar } from 'react-icons/ai';
import { TiExport } from 'react-icons/ti';
import { Categories, useLegendActions } from 'actions/legend';
import Tree from 'rc-tree';
import 'rc-tree/assets/index.css';
import Symbology from './symbology';
import VisualisationRule from './visualisationRules';
import { Menu, Item, useContextMenu } from 'react-contexify';
import 'react-contexify/ReactContexify.css';
import {
  MdClose,
  MdColorLens,
  MdFullscreenExit,
  MdOutlineRule,
} from 'react-icons/md';
import { RgbaColor } from 'react-colorful';
import { SymbologyModelRecord } from 'models/SymbologyModel';
import { SymbologyModel } from 'models/SymbologyModel';
import { LayerAttributeStyle } from 'models/LayerAttributeStyle';
import { LayerRuleStyleRecord } from 'models/LayerRuleStyle';
import './style.scss';
import Modal from 'react-modal';
import { getLegendStructure } from './getLegendStructure';
import { FiRefreshCw } from 'react-icons/fi';
import { BsExclamationLg } from 'react-icons/bs';
import { assetActions } from 'actions/asset';
import type { ContextMenuLayerNode } from 'types/layerRefresh';
import { type LayerExportProgress, normalizeLegendLayerKey, formatExportProgressPhaseLabel, formatExportProgressCountText } from 'utils/layerExportProgress';
import { RC_TREE_MOTION } from 'utils/rcTreeMotion';
import { safeLocalStorageSetItem } from 'utils/safeLocalStorage';
import {
  FAVOURITES_CHANGED_EVENT,
  type FavouriteLayerNode,
  type FavouritesChangedDetail,
  dispatchFavouritesChanged,
  getFavouriteDisplayNodes,
  loadFavouritesMap,
  saveFavouritesMap,
  toggleFavourite,
} from 'utils/favouritesStorage';
import {
  dispatchSdmSetRecipeVisibility,
  isSdmPreconfiguredFavouriteKey,
  parseSdmPreconfiguredFavouriteRecipeId,
} from 'containers/SurveyDataManager/components/AvailableViewsPanel/sdmFavouriteKeys';
import { isSurveyCoverageFavouriteNode } from 'utils/layerEndPointUrl';
//@ts-ignore
import debounce from 'lodash/debounce';
import Draggable from 'react-draggable';
// import type { CheckInfo } from "rc-tree/lib/interface";


declare type Key = React.Key;

interface Props {
  onNodeChecked: (
    checkedNodes: Array<any>,
    currentSelectedNode?: any,
    refresh?: boolean,
  ) => void;
  onNodeCheckedSurveyManager?: (
    checkedNodes: Array<any>,
    currentSelectedNode?: any,
  ) => void;
  onSurveyCoverageFavouriteSync?: (layerKey: string, checked: boolean) => void;
  setLayerStyle: (
    key: string,
    color: RgbaColor,
    width: number,
    size: number,
    lineStyle: string,
    markerStyle: string,
    angle: number,
  ) => void;
  setLayerVisualisationRules: (
    key: string,
    type: string,
    style: SymbologyModel,
    layerAttributeStyles: LayerAttributeStyle[],
  ) => void;
  zoomToSection: (key: string) => void;
  bringLayerToTop: (layerKey: string) => void;
  styles: SymbologyModelRecord;
  layerRulesStyles: LayerRuleStyleRecord;
  setCheckedNodesState: (p: any[]) => void;
  handleContextMenuRefreshClicked: (
    selectedKeys: Key[],
    contextNode?: ContextMenuLayerNode | null,
  ) => void;
  setTreeData: React.Dispatch<React.SetStateAction<any[]>>;
  treeData: any[];
  checkedNodesState: any[];
  layerRefreshLoading: { [key: string]: { isLoading: boolean; hasError: boolean; errorMessage?: string } };
  setLayerRefreshLoading: React.Dispatch<React.SetStateAction<{ [key: string]: { isLoading: boolean; hasError: boolean; errorMessage?: string } }>>;
  showDirection: boolean;
  setShowDirection: (value: boolean) => void;
  exportToShapefile: (targetLayerNode?: any, legendTreeData?: readonly any[]) => Promise<void>;
  cancelShapefileExport: () => void;
  isExporting: boolean;
  exportProgress?: LayerExportProgress | null;
  setLayerLabels?: (key: string, enabled: boolean) => void;
  layerData: Record<string, any>;
  checkedKeys: Key[];
  setCheckedKeys: React.Dispatch<React.SetStateAction<Key[]>>;
}

const MENU_ID = 'legend-menu';
const isSupportedGeometryType = (geometryType?: string) =>
  ['point', 'polyline', 'line', 'polygon'].includes(geometryType || '');

const Legend: React.FC<Props> = ({
  onNodeChecked,
  onNodeCheckedSurveyManager,
  onSurveyCoverageFavouriteSync,
  setLayerStyle,
  setLayerVisualisationRules,
  bringLayerToTop,
  zoomToSection,
  styles = {},
  layerRulesStyles = {},
  setCheckedNodesState,
  handleContextMenuRefreshClicked,
  setTreeData,
  treeData,
  checkedNodesState,
  layerRefreshLoading,
  setLayerRefreshLoading,
  showDirection,
  setShowDirection,
  exportToShapefile,
  cancelShapefileExport,
  isExporting,
  exportProgress,
  setLayerLabels,
  layerData,
  checkedKeys,
  setCheckedKeys,
}) => {
  const legendActions = useLegendActions();
  const [loading, setLoading] = useState(() => treeData.length === 0);
  const [symbologyOpened, setSymbologyOpened] = useState(false);
  const [visualisationRulesOpened, setVisualisationRulesOpened] =
    useState(false);
  const [inDragMode, setInDragMode] = useState(false);
  const [symbologyData, setSymbologyData] = useState({
    key: '',
    category: '',
    title: '',
  });
  const [visualisationRulesData, setVisualisationRulesData] = useState({
    key: '',
    category: '',
    title: '',
    parameterId: -1,
  });
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([]);
  const [contextMenuNode, setContextMenuNode] =
    useState<ContextMenuLayerNode | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [favourites, setFavourites] = useState<Map<string, FavouriteLayerNode>>(
    () => loadFavouritesMap(),
  );
  const [favouritesIsAdding, setFavouritesIsAdding] = useState<boolean | null>(
    null,
  );
  const skipNextFavouritesEventRef = useRef(false);
  const [hideVisRules, setHideVisRules] = useState<boolean>(false);
  const [visRulesNoAttr, setVisRulesNoAttr] = useState<string | null>(null);
  const [symbologyNoGeom, setSymbologyNoGeom] = useState<string | null>(null);
  const [windowSize, setWindowSize] = useState({ innerWidth: 0, innerHeight: 0 });

  useEffect(() => {
    const clearSelection = () => setSelectedKeys([]);
    window.addEventListener('surveymanager:layer-selected', clearSelection);
    return () => window.removeEventListener('surveymanager:layer-selected', clearSelection);
  }, []);

  useEffect(() => {
    const onFavouritesChanged = (event: Event) => {
      if (skipNextFavouritesEventRef.current) {
        skipNextFavouritesEventRef.current = false;
        return;
      }
      const detail = (event as CustomEvent<FavouritesChangedDetail>).detail;
      if (!detail) {
        return;
      }
      setFavourites(new Map(detail.entries));
      setFavouritesIsAdding(detail.isAdding);
    };
    window.addEventListener(FAVOURITES_CHANGED_EVENT, onFavouritesChanged);
    return () =>
      window.removeEventListener(FAVOURITES_CHANGED_EVENT, onFavouritesChanged);
  }, []);

  const { show } = useContextMenu({ id: MENU_ID });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
      });
    }
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const customStyles = {
    content: {
      left: `${(windowSize.innerWidth - 378 - 203) / 2}px`,
      top: `${(windowSize.innerHeight - 425) / 2}px`,
    },
  };

  const validateKeys = (keys: Key[], treeData: any[]): Key[] => {
    const validKeys = new Set();
    const traverseNodes = (nodes: any[]) => {
      nodes.forEach((node) => {
        validKeys.add(node.key);
        if (node.children) traverseNodes(node.children);
      });
    };
    traverseNodes(treeData);
    return keys.filter((key) => validKeys.has(key));
  };

  const getParentKeys = (
    key: Key,
    nodes: any[],
    parentKeys: Key[] = [],
  ): Key[] => {
    for (const node of nodes) {
      if (node.children) {
        for (const child of node.children) {
          if (child.key === key) return [...parentKeys, node.key];
          const found = getParentKeys(key, node.children, [
            ...parentKeys,
            node.key,
          ]);
          if (found.length) return found;
        }
      }
    }
    return [];
  };

  const getAllParentKeys = (checkedKeys: Key[], treeData: any[]): Key[] => {
    const parentKeys = new Set<Key>();
    checkedKeys.forEach((key) => {
      const parents = getParentKeys(key, treeData);
      parents.forEach((parentKey) => parentKeys.add(parentKey));
    });
    return Array.from(parentKeys);
  };

  const removeFavouritesNode = (nodes: any[]): any[] => {
    return nodes.filter((node) => {
      if (
        node.title === 'Favourites' &&
        (!node.children || node.children.length === 0)
      )
        return false;
      if (node.children) node.children = removeFavouritesNode(node.children);
      return true;
    });
  };

  const removeDuplicateNodes = (nodes: any[]): any[] => {
    const seenKeys = new Set();
    return nodes.reduce((acc: any[], node: any) => {
      if (!seenKeys.has(node.key)) {
        seenKeys.add(node.key);
        const newNode = { ...node };
        if (newNode.children)
          newNode.children = removeDuplicateNodes(newNode.children);
        acc.push(newNode);
      }
      return acc;
    }, []);
  };

  // const debouncedOnNodeChecked = useCallback(debounce((checkedNodes: any[], currentSelectedNode: any) => {
  //   onNodeChecked(checkedNodes, currentSelectedNode);
  // }, 1000), []);

  const fetchAndSetNetworkAttributes = async (
    attributeName: string,
    refreshData: boolean = false,
  ) => {
    try {
      const result = await assetActions().getNetworkAttributes(
        attributeName,
        refreshData,
      );
      return result;
    } catch (error) {
      console.error('Error fetching network attributes:', error);
      alert('Error fetching network attributes');
      return [];
    }
  };

  const fetchAndSetAssetTypeAttributes = async (
    itemKey: string,
    parameterId: number,
  ) => {
    try {
      const result = await assetActions().getAssetTypeAttributes(
        itemKey.split('+')[0],
        itemKey.split('+')[1],
        itemKey,
        parameterId,
      );
      const objResult = Array.isArray(result) ? result : [result];
      return objResult.filter((x) => x.hasLookups || x.dataType === 'Integer');
    } catch (error) {
      console.error('Error fetching asset type attributes:', error);
      return [];
    }
  };

  useEffect(() => {
    const currentTenant = localStorage.getItem('tenantId');
    const currentUser = JSON.parse(
      localStorage.getItem('userData') || '{}',
    ) as { username?: string };
    if (!currentTenant || !currentUser) {
      if (treeData.length > 0) {
        setLoading(false);
      }
      return;
    }

    const savedFavouritesMap = loadFavouritesMap();
    if (savedFavouritesMap.size > 0) {
      setFavourites(savedFavouritesMap);
    }

    const onGetLegend = async () => {
      try {
        const legendResponse = await legendActions.getLegend(false);
        if (!legendResponse) throw new Error('No legend response received');

        const trData = getLegendStructure(legendResponse);
        const savedTreeDataKey = `treeData_${currentTenant}_${currentUser?.username}`;

        if (savedFavouritesMap.size > 0) {
          const favs = getFavouriteDisplayNodes(savedFavouritesMap).map((node) => ({
                      ...node,
                      key: `favourites_${node.key}`,
                      selectable: true,
                      category: "Favourites",
                    }));
          if (favs.length > 0) {
            trData.unshift({
              key: 'favourites-node',
              title: 'Favourites',
              selectable: false,
              isLeaf: false,
              checkable: false,
              children: favs,
            });
          }
        }

        setTreeData(trData);
        safeLocalStorageSetItem(savedTreeDataKey, JSON.stringify(trData));
        setLoading(false);
      } catch (err) {
        console.error('Error fetching legend:', err);
        setLoading(false);
      }
    };

    if (treeData.length > 0) {
      setLoading(false);
      return;
    }

    onGetLegend();
  }, []);

  useEffect(() => {
    if (treeData.length > 0) {
      setLoading(false);
    }
  }, [treeData.length]);

  useEffect(() => {
    if (treeData.length > 0) {
      let updatedTreeData = [...treeData];
      const currentTenant = localStorage.getItem('tenantId');
      const currentUser = JSON.parse(
        localStorage.getItem('userData') || '{}',
      ) as { username?: string };
      const savedTreeDataKey = `treeData_${currentTenant}_${currentUser?.username}`;

      const favoritesNodeIndex = updatedTreeData.findIndex(
        (node) => node.title === 'Favourites',
      );
      const favChildren = getFavouriteDisplayNodes(favourites).map((node) => ({
        ...node,
        key: `favourites_${node.key}`,
        selectable: true,
        category: 'Favourites',
      }));

      if (favChildren.length > 0) {
        if (favoritesNodeIndex >= 0) {
          updatedTreeData[favoritesNodeIndex] = {
            ...updatedTreeData[favoritesNodeIndex],
            children: favChildren,
          };
          if (favoritesNodeIndex > 0) {
            const [favouritesNode] = updatedTreeData.splice(favoritesNodeIndex, 1);
            updatedTreeData.unshift(favouritesNode);
          }
        } else if (favouritesIsAdding !== null || favourites.size > 0) {
          updatedTreeData.unshift({
            key: 'favourites-node',
            title: 'Favourites',
            selectable: false,
            isLeaf: false,
            checkable: false,
            children: favChildren,
          });
        }
      } else if (
        favourites.size === 0 &&
        favoritesNodeIndex !== -1 &&
        favouritesIsAdding !== null
      ) {
        updatedTreeData.splice(favoritesNodeIndex, 1);
      }

      updatedTreeData = removeFavouritesNode(updatedTreeData);
      setTreeData(updatedTreeData);

      if (currentTenant && currentUser) {
        saveFavouritesMap(favourites);
        safeLocalStorageSetItem(savedTreeDataKey, JSON.stringify(updatedTreeData));
      }
    }
  }, [favourites, favouritesIsAdding]);

  useEffect(() => {
    const networkKey = treeData.find(
      (data) => data.category === 'Network' && data.isChecked === true,
    );
    if (networkKey) setCheckedNodesState([networkKey]);
  }, [treeData]);

  useEffect(() => {
    const checkedKey = treeData.find((data) => data.isChecked === true);
    if (checkedKey) setCheckedNodesState([checkedKey]);
  }, [treeData]);

  const getNodes = (node: any): any[] => {
    if (node?.children?.length > 0 && node.children[0].isLeaf)
      return node.children;
    if (node?.children?.length > 0)
      return node.children.map((x: any) => getNodes(x)).flat();
    return [];
  };

  const resolveNodeEndPoint = (node: {
    readonly endPoint?: string;
    readonly endpoint?: string;
  }): string | undefined => node.endPoint ?? node.endpoint;

  const dispatchLeafLayerCheck = (
    node: {
      readonly title?: string;
      readonly key?: Key;
      readonly endPoint?: string;
      readonly endpoint?: string;
      readonly category?: string;
      readonly type?: string;
    },
    checked: boolean,
    currentSelectedNode?: {
      readonly key: string;
      readonly title?: string;
      readonly category?: string;
      readonly endPoint?: string;
    },
  ): void => {
    const baseKey = String(node.key ?? '').replace(/^favourites_/, '');
    const endPoint = resolveNodeEndPoint(node);
    const payload = [
      {
        checked,
        title: node.title,
        key: baseKey,
        endPoint,
        category: node.category,
        type: node.type,
      },
    ];

    if (
      onNodeCheckedSurveyManager &&
      isSurveyCoverageFavouriteNode({ type: node.type, endPoint })
    ) {
      onNodeCheckedSurveyManager(payload, currentSelectedNode);
      onSurveyCoverageFavouriteSync?.(baseKey, checked);
      return;
    }

    onNodeChecked(payload, currentSelectedNode);
  };

  const nodeSelected = (selectedKeys: React.Key[], info: { event: "select"; selected: boolean; node: any; selectedNodes: any[]; nativeEvent: MouseEvent; }) => {
    if (info.selected) {
      const baseKey = String(info.node.key).replace('favourites_', '');
      bringLayerToTop(baseKey);
      setSelectedKeys([info.node.key]);
      window.dispatchEvent(new CustomEvent('legend:layer-selected'));

      if (!info.node.checked) {
        if (isSdmPreconfiguredFavouriteKey(baseKey)) {
          const recipeId = parseSdmPreconfiguredFavouriteRecipeId(baseKey);
          if (recipeId) {
            dispatchSdmSetRecipeVisibility(recipeId, true);
          }
          setCheckedKeys((x) => [
            ...new Set([
              ...x.map(String),
              info.node.key,
              info.node.key.startsWith('favourites_')
                ? info.node.key.replace('favourites_', '')
                : `favourites_${info.node.key}`,
            ]),
          ]);
          return;
        }
        dispatchLeafLayerCheck(
          info.node,
          true,
          {
            key: baseKey,
            title: info.node.title,
            category: info.node.category,
            endPoint: resolveNodeEndPoint(info.node),
          },
        );
        setCheckedKeys((x) => [
          ...x,
          info.node.key,
          info.node.key.startsWith('favourites_')
            ? info.node.key.replace('favourites_', '')
            : `favourites_${info.node.key}`,
        ]);
      } else if (!isSdmPreconfiguredFavouriteKey(baseKey)) {
        if (
          onNodeCheckedSurveyManager &&
          isSurveyCoverageFavouriteNode(info.node)
        ) {
          onNodeCheckedSurveyManager([], {
            key: baseKey,
            title: info.node.title,
            category: info.node.category,
            endPoint: resolveNodeEndPoint(info.node),
          });
          onSurveyCoverageFavouriteSync?.(baseKey, false);
        } else {
          onNodeChecked([], {
            key: baseKey,
            title: info.node.title,
            category: info.node.category,
            endPoint: resolveNodeEndPoint(info.node),
          });
        }
      }
    } else {
      setSelectedKeys([]);
    }
  };


  const nodeChecked = (
    checked: React.Key[] | { checked: React.Key[]; halfChecked: React.Key[] },
    info: any
  ) => {
    // @ts-ignore
    const favourites_checkedKeys = checkedKeys.filter((item) => item.startsWith('favourites_'));

    // @ts-ignore
    const uniqueCombined = [...new Set([...checked, ...favourites_checkedKeys])];
    // @ts-ignore
    let localChecked = [...uniqueCombined];
    if (info.node.key.startsWith("favourites_")) {
      if (info.checked) {
        // @ts-ignore
        localChecked.push(info.node.key.replace('favourites_', ''));
      } else {
        localChecked = localChecked.filter((item) => item !== info.node.key.replace('favourites_', ''));
        localChecked = localChecked.filter((item) => item !== info.node.key);
      }
    } else {
      if (info.checked) {
        // @ts-ignore
        localChecked.push(`${'favourites_' + info.node.key}`);
      } else {
        localChecked = localChecked.filter((item) => item !== `${'favourites_' + info.node.key}`);
      }
    }
    setCheckedKeys(localChecked);
    if (info.node.isLeaf) {
      if (info.checked === false && info.node.key === (selectedKeys.length > 0 ? selectedKeys[0] : null)) {
        setSelectedKeys([]);
      }

      const baseKey = String(info.node.key).replace('favourites_', '');
      if (isSdmPreconfiguredFavouriteKey(baseKey)) {
        const recipeId = parseSdmPreconfiguredFavouriteRecipeId(baseKey);
        if (recipeId) {
          dispatchSdmSetRecipeVisibility(recipeId, Boolean(info.checked));
        }
        return;
      }

      dispatchLeafLayerCheck(info.node, Boolean(info.checked));
    } else {
      const nodes = getNodes(info.node).map((item: any) => ({
        checked: info.checked,
        title: item.title,
        key: item.key.replace('favourites_', ''),
        endPoint: item.endPoint ?? item.endpoint,
        category: item.category,
        type: item.type,
      }));
      const nonSdmNodes = nodes.filter(
        (node: { key: string }) => !isSdmPreconfiguredFavouriteKey(node.key),
      );
      for (const node of nodes) {
        if (!isSdmPreconfiguredFavouriteKey(node.key)) {
          continue;
        }
        const recipeId = parseSdmPreconfiguredFavouriteRecipeId(node.key);
        if (recipeId) {
          dispatchSdmSetRecipeVisibility(recipeId, Boolean(info.checked));
        }
      }
      const surveyCoverageNodes = nonSdmNodes.filter((node) =>
        isSurveyCoverageFavouriteNode(node),
      );
      const legendNodes = nonSdmNodes.filter(
        (node) => !isSurveyCoverageFavouriteNode(node),
      );
      if (surveyCoverageNodes.length > 0 && onNodeCheckedSurveyManager) {
        onNodeCheckedSurveyManager(surveyCoverageNodes);
        for (const node of surveyCoverageNodes) {
          onSurveyCoverageFavouriteSync?.(
            String(node.key),
            Boolean(info.checked),
          );
        }
      }
      if (legendNodes.length > 0) {
        onNodeChecked(legendNodes);
      }
    }
  };

  const onNodeDoubleClick = (e: any, node: any) => {
    if (node.isLeaf && node.isChecked) {
      const resolvedKey = node.key.replace('favourites_', '');
      const geometryType = layerData?.[resolvedKey]?.geometryType;
      if (!isSupportedGeometryType(geometryType)) {
        setSymbologyNoGeom('No geometry found for this layer');
        return;
      }
      setSymbologyData({
        key: resolvedKey,
        category: node.category,
        title: node.title,
      });
      setSymbologyOpened(true);
    }
  };

  const onRightClick = (info: { event: React.MouseEvent; node: any }) => {
    setContextMenuNode(info.node);
    if (info.node.isLeaf && !info.node.checked) {
      const baseKey = String(info.node.key).replace('favourites_', '');
      setCheckedKeys((ch) => [
        ...new Set([
          ...ch,
          info.node.key,
          info.node.key.startsWith('favourites_')
            ? info.node.key.replace('favourites_', '')
            : `favourites_${info.node.key}`,
        ]),
      ]);
      if (isSdmPreconfiguredFavouriteKey(baseKey)) {
        const recipeId = parseSdmPreconfiguredFavouriteRecipeId(baseKey);
        if (recipeId) {
          dispatchSdmSetRecipeVisibility(recipeId, true);
        }
      } else {
        dispatchLeafLayerCheck(info.node, true);
      }
      bringLayerToTop(baseKey);
      setSelectedKeys([baseKey]);
      setSelectedCategory(info.node.category);
      window.dispatchEvent(new CustomEvent('legend:layer-selected'));
    }
    setHideVisRules(info.node.key.split('+')[0] === 'Surveys');
    if (info.node.isLeaf && info.node.checked) {
      show({
        event: info.event,
        props: {
          key: info.node.key.replace('favourites_', ''),
          category: info.node.category,
          title: info.node.title,
          parameterId: info.node.parameterId,
        },
      });
      bringLayerToTop(info.node.key.replace('favourites_', ''));
      setSelectedKeys([info.node.key.replace('favourites_', '')]);
      setSelectedCategory(info.node.category);
      window.dispatchEvent(new CustomEvent('legend:layer-selected'));
    }
  };

  const onDragEnd = () => setInDragMode(false);
  const onDragStart = () => setInDragMode(true);

  const handleOpenSymbology = (ev: any) => {
    const geometryType = layerData?.[ev.props.key]?.geometryType;
    if (!isSupportedGeometryType(geometryType)) {
      setSymbologyNoGeom('No geometry found for this layer');
      return;
    }
    setSymbologyData({ key: ev.props.key, category: ev.props.category, title: ev.props.title });
    setSymbologyOpened(true);
  };

  const handleOpenVisualisationRules = async (ev: any) => {
    const geometryType = layerData?.[ev.props.key]?.geometryType;
    if (!isSupportedGeometryType(geometryType)) {
      setVisRulesNoAttr('No geometry found for this layer');
      return;
    }
    setVisualisationRulesData({ key: ev.props.key, category: ev.props.category, title: ev.props.title, parameterId: ev.props.parameterId });
    try {
      const result =
        ev.props.category === 'Network'
          ? await fetchAndSetNetworkAttributes(ev.props.title)
          : await fetchAndSetAssetTypeAttributes(
              ev.props.key,
              ev.props.parameterId,
            );
      if (result.length === 0) {
        setVisualisationRulesOpened(false);
        setVisRulesNoAttr('No attributes found for this layer');
        return;
      }
      setVisualisationRulesOpened(true);
    } catch (error) {
      alert('Error fetching attributes');
    }
  };

  const closeModal = () => setVisRulesNoAttr(null);
  const closeSymbologyNoGeomModal = () => setSymbologyNoGeom(null);

  const handleZoomToSection = (ev: any) => zoomToSection(ev.props.key);

  const findParentNode = (key: string, nodes: any, category: any = null) => {
    for (const node of nodes) {
      if (node.children) {
        const currentCategory = category || node;
        for (const child of node.children) {
          if (child.key === key)
            return { ...node, categoryName: currentCategory?.title };
          const found: any = findParentNode(
            key,
            node.children,
            currentCategory,
          );
          if (found) return found;
        }
      }
    }
    return null;
  };

  const handleImageClick = (
    nodeData: FavouriteLayerNode,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    const parentNode = findParentNode(nodeData.key, treeData);
    const parentName = parentNode ? String(parentNode.title) : 'Root';
    const { favourites: nextFavourites, isAdding } = toggleFavourite(
      favourites,
      nodeData,
      parentName,
    );
    setFavourites(nextFavourites);
    setFavouritesIsAdding(isAdding);
    skipNextFavouritesEventRef.current = true;
    dispatchFavouritesChanged(nextFavourites, isAdding);
  };

  const handleExportClick = () => {
    if (!contextMenuNode) {
      return;
    }
    exportToShapefile(contextMenuNode, treeData);
  };

  const titleRender = (nodeData: any) => {
    let isSelectedKeysRefreshing: {
      [key: string]: {
        [x: string]: any;
        isLoading: boolean;
        hasError: boolean;
      };
    } = {};
    if (layerRefreshLoading.hasOwnProperty(nodeData.key)) {
      isSelectedKeysRefreshing[nodeData.key] =
        layerRefreshLoading[nodeData.key];
    }

    const isChecked = checkedKeys.includes(nodeData.key);
    const parentKeys = getAllParentKeys(checkedKeys, treeData);
    const isParentChecked = parentKeys.includes(nodeData.key);
    const isSelected =
      (isChecked || isParentChecked) && nodeData.title !== 'Network';

    const normalizedNodeKey = normalizeLegendLayerKey(String(nodeData.key));
    const isExportingThisLayer =
      exportProgress != null &&
      normalizeLegendLayerKey(exportProgress.layerKey) === normalizedNodeKey;

    return (
      <div
        className={`title_render--wrapper${isExportingThisLayer ? ' title_render--wrapper--exporting' : ''}`}
      >
        {isExportingThisLayer && (
          <div
            className={`legend-export-progress${exportProgress.hasError ? ' legend-export-progress--error' : ''}`}
            title={exportProgress.message}
            aria-label={exportProgress.message}
          >
            <div
              className="legend-export-progress__fill"
              style={{ width: `${exportProgress.percent}%` }}
            />
          </div>
        )}
        <div className="title_render--container">
        <div
          className={`title_render--container_title ${isSelected ? 'selected-title' : ''}`}
          title={`${nodeData.title} ${nodeData.category === 'Favourites' ? `\n~${nodeData.categoryName}~` : ''}${isExportingThisLayer ? `\n${exportProgress.message}` : ''}`}
        >
          <span className="title_render--container_title-text">{nodeData.title}</span>
          {isExportingThisLayer && (() => {
            const exportCountText = formatExportProgressCountText(exportProgress);
            return exportCountText ? (
              <span className="legend-export-progress__label">
                {exportCountText}
              </span>
            ) : null;
          })()}
          {isExportingThisLayer && !exportProgress.cancelled && !exportProgress.hasError && (
            <button
              type="button"
              className="legend-export-progress__cancel"
              title="Cancel export"
              aria-label="Cancel export"
              onClick={(event) => {
                event.stopPropagation();
                cancelShapefileExport();
              }}
            >
              <MdClose size={14} />
            </button>
          )}
        </div>
        {nodeData.isLeaf && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            {(isSelectedKeysRefreshing?.[nodeData.key]?.isLoading ||
              isSelectedKeysRefreshing?.['network-layer']?.isLoading) && (
              <FiRefreshCw className="rotating" />
            )}
            {(isSelectedKeysRefreshing?.[nodeData.key]?.hasError ||
              isSelectedKeysRefreshing?.['network-layer']?.hasError) &&
              (() => {
                const errorMsg =
                  isSelectedKeysRefreshing?.[nodeData.key]?.errorMessage ||
                  isSelectedKeysRefreshing?.['network-layer']?.errorMessage;
                console.error('Legend layer error:', errorMsg);
                return (
                  <span
                    title={errorMsg}
                    style={{ display: 'inline-flex', alignItems: 'center' }}
                  >
                    <BsExclamationLg color="red" size={20} />
                  </span>
                );
              })()}
            {favourites.has(nodeData.key) ? (
              <AiFillStar
                className="favourites_star_img"
                color="#FFC845"
                size={19}
                onClick={(event) => handleImageClick(nodeData, event)}
              />
            ) : (
              <AiOutlineStar
                className="favourites_star_img"
                size={19}
                onClick={(event) => handleImageClick(nodeData, event)}
              />
            )}
          </div>
        )}
        </div>
      </div>
    );
  };

  const VisRulesNoAttrModal = ({
    VisRulesNoAttrText,
    title,
    onClose,
    isOpen,
  }: {
    VisRulesNoAttrText: string | null;
    title: string;
    onClose: () => void;
    isOpen: boolean;
  }) => {
    if (!VisRulesNoAttrText) return null;
    return (
      <Modal
        ariaHideApp={false}
        isOpen={isOpen}
        bodyOpenClassName={null}
        portalClassName="react-modal-container"
        overlayClassName="react-modal-overlay"
        className="react-modal-wrapper"
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}
        style={customStyles}
      >
        <div className="react-modal symbology-modal">
          <div className="modal-header" style={{ cursor: 'auto' }}>
            <div className="modal-title-overlay">
              Visualisation rules: {title}
            </div>
            <div className="modal-header-buttons">
              <div className="modal-close" onClick={onClose}>
                <MdClose />
              </div>
            </div>
          </div>
          <div className="modal-content">
            <p style={{ margin: 'auto' }}>{VisRulesNoAttrText}</p>
          </div>
        </div>
      </Modal>
    );
  };

  const SymbologyNoGeomModal = ({
    SymNoGeoText,
    title,
    onClose,
    isOpen,
  }: {
    SymNoGeoText: string | null;
    title: string;
    onClose: () => void;
    isOpen: boolean;
  }) => {
    if (!SymNoGeoText) return null;
    return (
      <Modal
        ariaHideApp={false}
        isOpen={isOpen}
        bodyOpenClassName={null}
        portalClassName="react-modal-container"
        overlayClassName="react-modal-overlay"
        className="react-modal-wrapper"
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}
        style={customStyles}
      >
        <div className="react-modal symbology-modal">
          <div className="modal-header" style={{ cursor: 'auto' }}>
            <div className="modal-title-overlay">Symbology: {title}</div>
            <div className="modal-header-buttons">
              <div className="modal-close" onClick={onClose}>
                <MdClose />
              </div>
            </div>
          </div>
          <div className="modal-content">
            <p style={{ margin: 'auto' }}>{SymNoGeoText}</p>
          </div>
        </div>
      </Modal>
    );
  };

  return (
    <>
      {symbologyOpened && (
        <Symbology
          isOpen={symbologyOpened}
          closeModal={() => setSymbologyOpened(false)}
          itemKey={symbologyData.key}
          category={symbologyData.category}
          title={symbologyData.title}
          setLayerStyle={setLayerStyle}
          currentWidth={styles[symbologyData.key]?.currentWidth}
          currentSize={styles[symbologyData.key]?.currentSize}
          currentLineStyle={styles[symbologyData.key]?.currentLineStyle}
          currentMarkerStyle={styles[symbologyData.key]?.currentMarkerStyle}
          currentColor={styles[symbologyData.key]?.currentColor}
          currentAngle={styles[symbologyData.key]?.currentAngle}
          layerType={styles[symbologyData.key]?.type}
          showDirection={showDirection}
          setShowDirection={setShowDirection}
          setLayerLabels={setLayerLabels}
        />
      )}
      {visualisationRulesOpened ? (
        <VisualisationRule
          isOpen={visualisationRulesOpened}
          closeModal={() => setVisualisationRulesOpened(false)}
          itemKey={visualisationRulesData.key}
          category={visualisationRulesData.category}
          title={visualisationRulesData.title}
          parameterId={visualisationRulesData.parameterId}
          layerType={styles[visualisationRulesData.key]?.type}
          defaultStyle={styles[visualisationRulesData.key]}
          layerRulesStyles={layerRulesStyles[visualisationRulesData.key]}
          setLayerVisualisationRules={setLayerVisualisationRules}
          setVisualisationRulesOpened={setVisualisationRulesOpened}
          showDirection={showDirection}
          setShowDirection={setShowDirection}
        />
      ) : (
        <VisRulesNoAttrModal
          VisRulesNoAttrText={visRulesNoAttr}
          title={visualisationRulesData.title}
          onClose={closeModal}
          isOpen={!!visRulesNoAttr}
        />
      )}
      <SymbologyNoGeomModal
        SymNoGeoText={symbologyNoGeom}
        title={symbologyData.title}
        onClose={closeSymbologyNoGeomModal}
        isOpen={!!symbologyNoGeom}
      />
      {!inDragMode && (
        <Menu id={MENU_ID}>
          <Item onClick={handleOpenSymbology}>
            <MdColorLens />
            Symbology
          </Item>
          {!hideVisRules && (
            <Item onClick={handleOpenVisualisationRules}>
              <MdOutlineRule />
              Visualisation Rules
            </Item>
          )}
          <Item onClick={handleZoomToSection}>
            <MdFullscreenExit />
            Zoom to extent
          </Item>
          {contextMenuNode && (
            <Item
              onClick={!isExporting ? handleExportClick : undefined}
              style={{
                pointerEvents: isExporting ? 'none' : 'auto',
                opacity: isExporting ? 0.6 : 1,
              }}
            >
              <TiExport />{' '}
              {isExporting ? (
                <>
                  {exportProgress
                    ? formatExportProgressPhaseLabel(exportProgress)
                    : 'Exporting…'}{' '}
                  <FiRefreshCw
                    className="rotating"
                    style={{ marginLeft: 'auto' }}
                  />
                </>
              ) : (
                'Export to Shapefile'
              )}
            </Item>
          )}
          <Item onClick={() => {
            handleContextMenuRefreshClicked(selectedKeys, contextMenuNode);
          }}>
            <FiRefreshCw size={10} style={{ marginRight: '5px' }} />Refresh
          </Item>
        </Menu>
      )}
      <div className="xa-legend-container">
        {loading ? (
          <div className="xa-legend-status" role="status" aria-live="polite">
            <FiRefreshCw className="rotating" aria-hidden />
            <span>Loading layers…</span>
          </div>
        ) : removeDuplicateNodes(treeData).length === 0 ? (
          <div className="xa-legend-status xa-legend-status--empty" role="status">
            <BsExclamationLg color="red" size={20} aria-hidden />
            <span>No data available</span>
          </div>
        ) : (
          <Tree
            showLine
            selectable
            checkable
            expandAction="click"
            motion={RC_TREE_MOTION}
            defaultExpandParent={false}
            autoExpandParent={false}
            defaultExpandAll={false}
            showIcon={false}
            treeData={removeDuplicateNodes(treeData)}
            onCheck={nodeChecked}
            onSelect={nodeSelected}
            checkedKeys={validateKeys(checkedKeys, treeData)}
            selectedKeys={validateKeys(selectedKeys, treeData)}
            onRightClick={onRightClick}
            onDoubleClick={onNodeDoubleClick}
            titleRender={titleRender}
            defaultExpandedKeys={['favourites-node']}
          />
        )}
      </div>
    </>
  );
};

export default Legend;
